import React, { useState, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, SimpleGrid, Spinner,
  Badge, Icon, Button, Alert, AlertIcon,
  Table, Thead, Tbody, Tr, Th, Td, useToast,
} from '@chakra-ui/react';
import { FiRefreshCw, FiZap, FiCheckCircle, FiXCircle, FiClock } from 'react-icons/fi';

const API_BASE = '/api/v1';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Token ${localStorage.getItem('token')}`,
});

function TrafficActivity() {
  const [log, setLog] = useState([]);        // array of check-run results
  const [checking, setChecking] = useState(false);
  const [autoInterval, setAutoInterval] = useState(null);
  const toast = useToast();

  const runCheck = useCallback(async () => {
    setChecking(true);
    try {
      const res = await fetch(`${API_BASE}/traffic/check/`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const entry = {
        time: new Date(),
        results: data.results,
        summary: data.summary,
      };
      setLog(prev => [entry, ...prev].slice(0, 50));
    } catch (err) {
      toast({ title: 'Health check failed', description: err.message, status: 'error', duration: 5000 });
    }
    setChecking(false);
  }, [toast]);

  const toggleAuto = useCallback(() => {
    if (autoInterval) {
      clearInterval(autoInterval);
      setAutoInterval(null);
      toast({ title: 'Auto-check stopped', status: 'info', duration: 2000 });
    } else {
      runCheck();
      const id = setInterval(runCheck, 30000);
      setAutoInterval(id);
      toast({ title: 'Auto-check started (30s)', status: 'success', duration: 2000 });
    }
  }, [autoInterval, runCheck, toast]);

  const latestRun = log.length > 0 ? log[0] : null;

  // Flatten all runs into a per-check activity log
  const flatLog = [];
  log.forEach(run => {
    run.results.forEach(r => {
      flatLog.push({ ...r, time: run.time });
    });
  });

  const latencyColor = (ms) => {
    if (ms == null) return 'red.500';
    if (ms < 300) return 'green.500';
    if (ms < 1000) return 'yellow.500';
    return 'red.500';
  };

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Traffic Activity</Heading>
          <Text color="gray.500">
            Health check activity log — {log.length} runs recorded
          </Text>
        </VStack>
        <HStack>
          <Button
            leftIcon={<FiZap />}
            colorScheme={autoInterval ? 'red' : 'green'}
            variant={autoInterval ? 'solid' : 'outline'}
            onClick={toggleAuto}
          >
            {autoInterval ? 'Stop Auto' : 'Start Auto (30s)'}
          </Button>
          <Button leftIcon={<FiRefreshCw />} colorScheme="blue" onClick={runCheck} isLoading={checking}>
            Check Now
          </Button>
        </HStack>
      </HStack>

      {/* Summary Cards */}
      {latestRun && (
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6} mb={8}>
          <Box bg="white" p={5} borderRadius="lg" boxShadow="md" borderLeft="4px solid" borderColor="green.400">
            <Text fontSize="sm" color="gray.500" fontWeight="semibold">Healthy</Text>
            <Text fontSize="2xl" fontWeight="bold" color="green.600">{latestRun.summary.healthy}</Text>
          </Box>
          <Box bg="white" p={5} borderRadius="lg" boxShadow="md" borderLeft="4px solid" borderColor="red.400">
            <Text fontSize="sm" color="gray.500" fontWeight="semibold">Unhealthy</Text>
            <Text fontSize="2xl" fontWeight="bold" color="red.600">{latestRun.summary.unhealthy}</Text>
          </Box>
          <Box bg="white" p={5} borderRadius="lg" boxShadow="md" borderLeft="4px solid" borderColor="blue.400">
            <Text fontSize="sm" color="gray.500" fontWeight="semibold">Avg Latency</Text>
            <Text fontSize="2xl" fontWeight="bold">
              {Math.round(latestRun.results.filter(r => r.latency_ms).reduce((a, b) => a + b.latency_ms, 0) / Math.max(1, latestRun.results.filter(r => r.latency_ms).length))} ms
            </Text>
          </Box>
          <Box bg="white" p={5} borderRadius="lg" boxShadow="md" borderLeft="4px solid" borderColor="purple.400">
            <Text fontSize="sm" color="gray.500" fontWeight="semibold">Total Checks</Text>
            <Text fontSize="2xl" fontWeight="bold">{flatLog.length}</Text>
          </Box>
        </SimpleGrid>
      )}

      {flatLog.length === 0 && !checking && (
        <Alert status="info" borderRadius="lg">
          <AlertIcon />
          <Text>Click <strong>Check Now</strong> or <strong>Start Auto</strong> to begin recording health check activity.</Text>
        </Alert>
      )}

      {checking && flatLog.length === 0 && (
        <Box textAlign="center" py={10}>
          <Spinner size="xl" color="blue.400" />
          <Text mt={4} color="gray.500">Running health checks…</Text>
        </Box>
      )}

      {/* Activity Log Table */}
      {flatLog.length > 0 && (
        <Box bg="white" borderRadius="lg" boxShadow="md" overflow="hidden">
          <Table variant="simple" size="sm">
            <Thead bg="gray.50">
              <Tr>
                <Th>Time</Th>
                <Th>Service</Th>
                <Th>Endpoint</Th>
                <Th>Status</Th>
                <Th>Latency</Th>
                <Th>Detail</Th>
              </Tr>
            </Thead>
            <Tbody>
              {flatLog.slice(0, 200).map((r, i) => (
                <Tr key={i} _hover={{ bg: 'gray.50' }}>
                  <Td>
                    <Text fontSize="xs" color="gray.500" fontFamily="mono">
                      {r.time.toLocaleTimeString()}
                    </Text>
                  </Td>
                  <Td><Text fontSize="sm" fontWeight="medium">{r.name}</Text></Td>
                  <Td><Text fontSize="xs" fontFamily="mono" color="gray.500">{r.endpoint}</Text></Td>
                  <Td>
                    <HStack spacing={1}>
                      <Icon
                        as={r.ok ? FiCheckCircle : FiXCircle}
                        color={r.ok ? 'green.500' : 'red.500'}
                        boxSize={4}
                      />
                      <Badge colorScheme={r.ok ? 'green' : 'red'} fontSize="xs">
                        {r.ok ? 'OK' : 'FAIL'}
                      </Badge>
                    </HStack>
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      <Icon as={FiClock} boxSize={3} color={latencyColor(r.latency_ms)} />
                      <Text fontSize="sm" color={latencyColor(r.latency_ms)} fontWeight="medium">
                        {r.latency_ms != null ? `${r.latency_ms} ms` : '—'}
                      </Text>
                    </HStack>
                  </Td>
                  <Td>
                    <Text fontSize="xs" color="gray.500" isTruncated maxW="200px">
                      {r.error || (r.status_code ? `HTTP ${r.status_code}` : '—')}
                    </Text>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

export default TrafficActivity;
